import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PreliminaryValuation = ({ 
  formData, 
  isCollapsed = false, 
  onToggleCollapse 
}) => {
  const [valuation, setValuation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confidence, setConfidence] = useState(0);

  // Calculate valuation based on form data
  useEffect(() => {
    const calculateValuation = () => {
      if (!formData.make || !formData.model || !formData.year) {
        setValuation(null);
        setConfidence(0);
        return;
      }

      setLoading(true);
      
      // Simulate API call delay
      setTimeout(() => {
        // Mock valuation calculation
        const baseValues = {
          toyota: { camry: 25000, corolla: 22000, prius: 28000, rav4: 32000, highlander: 38000 },
          honda: { civic: 23000, accord: 27000, crv: 30000, pilot: 35000, odyssey: 33000 },
          ford: { f150: 35000, escape: 26000, explorer: 32000, mustang: 30000, edge: 28000 }
        };

        const currentYear = new Date().getFullYear();
        const vehicleAge = currentYear - parseInt(formData.year);
        const baseValue = baseValues[formData.make]?.[formData.model] || 25000;
        
        // Depreciation calculation
        let estimatedValue = baseValue - (vehicleAge * 2000);
        
        // Mileage adjustment
        if (formData.mileage) {
          const mileage = parseInt(formData.mileage);
          const averageMileage = vehicleAge * 12000;
          const mileageDiff = mileage - averageMileage;
          estimatedValue -= (mileageDiff / 1000) * 100;
        }

        // Condition adjustment
        const conditionMultipliers = {
          excellent: 1.1,
          'very-good': 1.05,
          good: 1.0,
          fair: 0.85,
          poor: 0.65
        };
        
        if (formData.overallCondition) {
          estimatedValue *= conditionMultipliers[formData.overallCondition] || 1.0;
        }

        // Damage adjustments
        if (formData.damageTypes?.length > 0) {
          estimatedValue *= 0.9; // 10% reduction for damage
        }

        if (formData.mechanicalIssues?.length > 0) {
          estimatedValue *= 0.85; // 15% reduction for mechanical issues
        }

        // Calculate confidence based on available data
        let confidenceScore = 0;
        if (formData.make && formData.model && formData.year) confidenceScore += 30;
        if (formData.mileage) confidenceScore += 20;
        if (formData.overallCondition) confidenceScore += 25;
        if (formData.photos?.length >= 4) confidenceScore += 15;
        if (formData.vin?.length === 17) confidenceScore += 10;

        setValuation({
          estimated: Math.max(estimatedValue, 5000), // Minimum value
          range: {
            low: Math.max(estimatedValue * 0.85, 4000),
            high: estimatedValue * 1.15
          }
        });
        setConfidence(Math.min(confidenceScore, 100));
        setLoading(false);
      }, 1000);
    };

    calculateValuation();
  }, [formData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getConfidenceColor = () => {
    if (confidence >= 80) return 'text-success';
    if (confidence >= 60) return 'text-warning';
    return 'text-muted-foreground';
  };

  const getConfidenceLabel = () => {
    if (confidence >= 80) return 'High Confidence';
    if (confidence >= 60) return 'Medium Confidence';
    if (confidence >= 40) return 'Low Confidence';
    return 'More Info Needed';
  };

  if (isCollapsed) {
    return (
      <div className="fixed bottom-4 right-4 lg:relative lg:bottom-auto lg:right-auto lg:mb-6">
        <Button
          variant="default"
          onClick={onToggleCollapse}
          iconName="DollarSign"
          iconPosition="left"
          className="shadow-elevated lg:shadow-soft"
        >
          {valuation ? formatCurrency(valuation.estimated) : 'Get Estimate'}
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border p-6 sticky top-20">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Preliminary Valuation</h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggleCollapse}
          iconName="Minimize2"
          className="lg:hidden"
        />
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-sm text-muted-foreground">Calculating estimate...</p>
        </div>
      ) : valuation ? (
        <div className="space-y-4">
          {/* Main Estimate */}
          <div className="text-center p-4 bg-primary/5 rounded-lg border border-primary/20">
            <p className="text-sm text-muted-foreground mb-1">Estimated Value</p>
            <p className="text-3xl font-bold text-primary">
              {formatCurrency(valuation.estimated)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Range: {formatCurrency(valuation.range.low)} - {formatCurrency(valuation.range.high)}
            </p>
          </div>

          {/* Confidence Indicator */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">Estimate Confidence</span>
              <span className={`text-sm font-medium ${getConfidenceColor()}`}>
                {confidence}%
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-smooth ${
                  confidence >= 80 ? 'bg-success' : 
                  confidence >= 60 ? 'bg-warning' : 'bg-muted-foreground'
                }`}
                style={{ width: `${confidence}%` }}
              ></div>
            </div>
            <p className={`text-xs ${getConfidenceColor()}`}>
              {getConfidenceLabel()}
            </p>
          </div>

          {/* Improvement Tips */}
          {confidence < 80 && (
            <div className="bg-accent/10 border border-accent/20 rounded-lg p-3">
              <h4 className="text-sm font-medium text-foreground mb-2 flex items-center">
                <Icon name="TrendingUp" size={16} className="mr-2 text-accent" />
                Improve Your Estimate
              </h4>
              <ul className="text-xs text-muted-foreground space-y-1">
                {!formData.overallCondition && (
                  <li>• Complete the condition assessment</li>
                )}
                {(!formData.photos || formData.photos.length < 4) && (
                  <li>• Upload more vehicle photos</li>
                )}
                {!formData.vin && (
                  <li>• Enter your VIN number</li>
                )}
                {!formData.mileage && (
                  <li>• Add current mileage</li>
                )}
              </ul>
            </div>
          )}

          {/* Disclaimer */}
          <div className="text-xs text-muted-foreground bg-muted/50 rounded-lg p-3">
            <Icon name="Info" size={14} className="inline mr-1" />
            This is a preliminary estimate. Final offers may vary based on vehicle inspection and market conditions.
          </div>

          {/* Action Button */}
          <Button
            variant="outline"
            fullWidth
            iconName="Calculator"
            iconPosition="left"
            className="mt-4"
          >
            Request Official Appraisal
          </Button>
        </div>
      ) : (
        <div className="text-center py-8">
          <Icon name="Calculator" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h4 className="text-lg font-medium text-foreground mb-2">Get Your Estimate</h4>
          <p className="text-sm text-muted-foreground mb-4">
            Complete the basic vehicle information to see your preliminary valuation
          </p>
          <div className="space-y-2 text-xs text-muted-foreground">
            <div className="flex items-center justify-between">
              <span>Make & Model</span>
              <Icon 
                name={formData.make && formData.model ? "Check" : "X"} 
                size={16} 
                className={formData.make && formData.model ? "text-success" : "text-muted-foreground"} 
              />
            </div>
            <div className="flex items-center justify-between">
              <span>Year</span>
              <Icon 
                name={formData.year ? "Check" : "X"} 
                size={16} 
                className={formData.year ? "text-success" : "text-muted-foreground"} 
              />
            </div>
            <div className="flex items-center justify-between">
              <span>Mileage</span>
              <Icon 
                name={formData.mileage ? "Check" : "X"} 
                size={16} 
                className={formData.mileage ? "text-success" : "text-muted-foreground"} 
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PreliminaryValuation;